import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import { AppState } from '../app.state';
import { MediaPlayerState } from '../enum/media-player/media-player-state';
import { MediaRadio } from '../objects/media-player/media-radio';
import { playMedia } from '../player.actions';
import {
  selectAllRadiosSelector,
  selectStaticMediaPlayerSelector,
} from '../player.selectors';

/**
 * One shared subscription to the player state, exposed as signals.
 * Station cards read this to show a "now playing" indicator without each
 * card opening its own store subscription.
 */
@Injectable({ providedIn: 'root' })
export class NowPlayingService {
  private readonly store = inject(Store<AppState>);

  readonly state = toSignal(this.store.select(selectStaticMediaPlayerSelector));

  /** Stream URL of the station currently loaded in the player, if any. */
  readonly currentUrl = computed(
    () => (this.state()?.mediaContent as MediaRadio | undefined)?.url ?? null,
  );

  readonly currentName = computed(
    () => this.state()?.mediaContent?.name ?? null,
  );

  readonly isPlaying = computed(
    () => this.state()?.playerState === MediaPlayerState.PLAYING,
  );

  readonly isLoading = computed(
    () => this.state()?.playerState === MediaPlayerState.LOADING,
  );

  /** True when the given station is the one loaded in the player. */
  isCurrent(url: string, name: string): boolean {
    return this.currentUrl() === url || this.currentName() === name;
  }

  /** Start a random station — used by the "surprise me" shortcut. */
  async playRandom(): Promise<void> {
    const radios = await firstValueFrom(this.store.select(selectAllRadiosSelector));
    if (!radios?.length) {
      return;
    }
    const pick = radios[Math.floor(Math.random() * radios.length)];
    this.store.dispatch(
      playMedia({
        mediaContent: new MediaRadio(
          pick.name,
          pick.url,
          pick.tags,
          pick.country,
          pick.favicon,
          pick.bitrate,
        ),
      }),
    );
  }
}
